"use client";

import { Table } from "@tanstack/react-table";
import { X, FileSpreadsheet, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import { useStoreAccessFilterActions } from "../../filter/hooks/actions";
import { useStoreAccessFilterState } from "../../filter/hooks/filterState";
import { useExportExcel } from "../../hooks/useExportExcel";
import SelectFilters from "../select-filters";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  filterSlot?: React.ReactNode;
}

export function DataTableToolbar<TData>({
  table,
  filterSlot,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const { updateTabFilter } = useStoreAccessFilterActions();
  const { storeCode, activeTab, [activeTab]: tabFilters } =
    useStoreAccessFilterState();
  const { handleExport, isLoading: isExporting } = useExportExcel();

  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((value: string) => {
        updateTabFilter({ tab: activeTab, key: "search", value });
        updateTabFilter({ tab: activeTab, key: "page", value: 1 });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab],
  );

  useEffect(() => () => debouncedUpdateSearch.cancel(), [debouncedUpdateSearch]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedUpdateSearch(event.target.value);
  };

  const handleExportClick = () => {
    // TODO: Substituir pelo nome do usuário logado quando disponível
    const analystName = "Analista";
    handleExport(analystName);
  };

  return (
    <div className="flex flex-wrap items-end justify-between gap-2">
      <div className="flex flex-wrap items-end gap-2 min-w-0">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-muted-foreground">Buscar</span>
          <Input
            key={`${activeTab}-${storeCode}`}
            defaultValue={tabFilters.search}
            placeholder="CPF ou nome..."
            onChange={handleInputChange}
            className="h-8 w-[220px]"
          />
        </div>
        {filterSlot}
        <SelectFilters />
        {isFiltered && (
          <Button
            variant="ghost_disabled"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Resetar
            <X />
          </Button>
        )}
      </div>
      <div className="flex items-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportClick}
          disabled={!storeCode || isExporting}
          className="h-8 shrink-0"
        >
          {isExporting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Exportando...
            </>
          ) : (
            <>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Exportar Excel
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
